/**
 * V5 报告 mock —— **严格匹配后端契约 wrapper 形态**（docs/archive/v5/M5-integration-contract.md
 * 「API Contract」+ AI-HANDOFF.md 2026-05-20 backend 条目）。
 *
 * 数据来源：
 *  - LLM 文案字段：字面拷贝 backend/apps/reports/schemas/v5_report_example.json
 *  - scoring (overall/abilities/traits) / meta / user：前端合成 L2 人设，
 *    保持 strength/improvement 排名一致（passes validator）。
 *
 * 顶层 HTTP 包装：{status, report_version, is_fallback, user_report_v5: {...}}
 * fallback 变体见底部 mockReportFallbackPayload —— 用于前端验证降级 banner。
 *
 * ⚠️ 不要在这里扩展或修改字段；要改字段先回后端 schema。
 */

// ============================================================================
// LLM 文案字段
// ============================================================================
const llmNarrative = {
  headline_tag: '嵌入探索型｜已将 AI 融入日常工作，带队与治理能力仍需加强。',
  overall_summary:
    '你已能在日常工作中主动借助 AI 完成具体任务，整体处于惯用期水平。在借力使用和与 AI 协同上具备一定积累，但在带队赋能团队以及 AI 治理与风险把控上仍偏弱。下一步如果能补足带队引导 + 建立治理动作，整体水平会较自然地从惯用期迈向扩散期。',
  strength_abilities: ['ai_leverage', 'ai_symbiosis'],
  improvement_abilities: ['ai_governance', 'ai_team_leadership'],
  priority_actions: ['补足 AI 带队能力', '建立 AI 治理习惯', '练习跨角色协同方法'],
  key_findings: [
    'AI借力已达扩散期，是你当前最稳、用得最高频的能力。',
    'AI带队仍停在惯用期，个人用得好但还没带动团队。',
    'AI治理停在初用期，是当前向上跃迁的主要短板。',
  ],
  comprehensive_interpretation:
    '你目前处于 AI 应用的惯用期：能够主动把 AI 接入日常工作，在借力使用和协同互动上表现稳定，也愿意尝试新的用法。这是一个不错的起点。当前真正限制你继续提升的，是两件事：一是如何带动团队成员提升 AI 使用水平；二是如何在团队或组织层面建立 AI 使用的规范与治理机制。建议的切入路径是先从带动 1-2 名同事开始，再逐步建立团队 AI 使用的边界与流程——把这两点稳住，整体水平会较快地从惯用期迈向扩散期。',
  // 注：场景名 + icon_hint 在生产由后端 pool/LLM 决定；此处为示意，用已批准的精简文案，
  // 与设计预览 docs/preview/aileader-report-preview-optimized.html 对齐，方便 /preview 视觉核对。
  try_first_scenarios: [
    { name: '团队周报与汇报材料初稿', icon_hint: 'clipboard', reason: '结构化、重复度高，AI 可快速起草', usage: '让 AI 出初稿，你补关键判断与数据' },
    { name: '会议纪要与行动项拆解', icon_hint: 'users', reason: '信息量大，适合 AI 归纳整理', usage: '让 AI 提结论、待办、责任人与截止' },
    { name: '跨部门沟通邮件初稿', icon_hint: 'mail', reason: '格式固定，AI 起草省时', usage: '让 AI 拟稿，你定调性再发出' },
    { name: '行业与竞品动态速读', icon_hint: 'book-open', reason: '资料多，AI 能快速摘要', usage: '让 AI 提要点，关键结论自行核实' },
    { name: '管理决策前的资料梳理', icon_hint: 'folder', reason: '需要先把材料理清', usage: '让 AI 归类对比，决策仍由你做' },
    { name: '战略思路头脑风暴', icon_hint: 'lightbulb', reason: '发散环节适合 AI 协同', usage: '让 AI 给多角度选项，你筛选收敛' },
  ],
  be_cautious_scenarios: [
    { name: '对外正式表态与发言稿', icon_hint: 'megaphone', reason: '对外口径错了影响大', usage: 'AI 可拟稿，定稿必须人工把关' },
    { name: '关键经营决策的依据', icon_hint: 'scale', reason: '责任在己，不能由 AI 下判断', usage: 'AI 仅整理事实，结论由你负责' },
    { name: '人事任免与绩效结论', icon_hint: 'user-check', reason: '涉及人员利益与伦理', usage: 'AI 只辅助梳理，评价由管理者定' },
    { name: '涉及数据与事实的结论', icon_hint: 'bar-chart', reason: 'AI 可能编造数字', usage: '数据与事实一律人工复核' },
    { name: '敏感信息与隐私处理', icon_hint: 'lock', reason: '涉数据安全与合规', usage: '敏感信息不入 AI，必要时用私有环境' },
    { name: '合规与风险的最终判断', icon_hint: 'shield', reason: '强合规场景需专业裁量', usage: 'AI 仅供参考，最终判断由人定' },
  ],
  priority_improvements: [
    {
      title: '补足 AI 带队能力',
      why_to_do:
        '当前你的 AI 使用主要停留在个人层面，还没有形成带动团队成员提升的习惯。领导者的 AI 能力如果只停在自用，会让团队整体协同效率大打折扣。',
      how_to_do:
        '每周挑选一个自己跑通的 AI 用法分享给团队，观察团队成员的反应并帮助他们落地。定期开一次"AI 实践分享会"，让大家互相学习最新用法。',
    },
    {
      title: '建立 AI 治理习惯',
      why_to_do:
        '你目前对 AI 在团队中的使用缺乏系统性规范，容易出现数据安全隐患或输出质量参差不齐的问题。建立基础的 AI 治理框架能显著降低风险。',
      how_to_do:
        '为团队梳理一份简单的 AI 使用规范：明确哪些场景可以用、哪些数据不能传入 AI、哪些输出需要人工复核。每季度复审一次，随实践迭代更新。',
    },
    {
      title: '培养 AI 谋势视野',
      why_to_do:
        '你对 AI 重塑行业的趋势已有关注，但还停留在惯用期，尚未把行业动态转成对自己业务的判断，影响向更高阶的跃迁。',
      how_to_do:
        '每周固定浏览 1-2 份权威 AI 行业报告，记录与本业务客户、竞争相关的信号；每月输出一条"对我们意味着什么"的判断，带到团队讨论。',
    },
  ],
};

// ============================================================================
// scoring 块（前端合成；后端落地后由 calc_overall 真值替换）
// ============================================================================
const overall = {
  average: 2.59, l_grade: 'L2', l_grade_label: '惯用期', percentage: 53,
  absolute_grade: '待改进',  // percentage 53 (<60) → 待改进，与 map_to_absolute_grade 一致
  // 方案A 收口：最强/最弱力点名（与 abilities 最高/最低项对齐）
  strongest_ability: {
    code: 'ai_leverage', name: 'AI借力',
    average: 3.05, l_grade: 'L3', l_grade_label: '扩散期', percentage: 68,
  },
  weakest_ability: {
    code: 'ai_governance', name: 'AI治理',
    average: 1.60, l_grade: 'L1', l_grade_label: '初用期', percentage: 20,
  },
};

const match = {
  manager_level: 'S2',
  is_match: false,
  gaps: [{ force_code: 'ai_team_leadership', expected_l: 'L2', actual_l: 'L1' }],
  weighted_score: 50,
  divergence: 'needs_overall',
};

const abilities = [
  // 顺序遵循 dimensions/defaults.py sort_order（5 力，无 layer 字段）
  // 示意数据：时期(l_grade) 与百分位分位对齐（percentage ≈ (average-1)/3*100），
  // 让条形/阶梯可视化自洽（避免「65 却标惯用期」这类错位）；strength=借力/共生(最高2)、
  // improvement=治理/带队(最低2) 仍成立，通过 validator 排名校验。
  { code: 'ai_leverage',            name: 'AI借力',   average: 3.05, l_grade: 'L3', l_grade_label: '扩散期', percentage: 68 },
  { code: 'ai_symbiosis',           name: 'AI共生',   average: 2.74, l_grade: 'L3', l_grade_label: '扩散期', percentage: 58 },
  { code: 'ai_team_leadership',     name: 'AI带队',   average: 2.14, l_grade: 'L2', l_grade_label: '惯用期', percentage: 38 },
  { code: 'ai_governance',          name: 'AI治理',   average: 1.60, l_grade: 'L1', l_grade_label: '初用期', percentage: 20 },
  { code: 'ai_strategic_foresight', name: 'AI谋势',   average: 2.38, l_grade: 'L2', l_grade_label: '惯用期', percentage: 46 },
];

// ============================================================================
// user_report_v5 内部对象（contract 文档 §user_report_v5 payload schema）
// ============================================================================
function makeUserReportV5({ isFallback = false, validatorFailures = [] } = {}) {
  return {
    meta: {
      report_version: 5,
      assessment_id: 0,
      depth: 'quick',
      depth_label: '基础版',
      answered_count: 30,
      total_questions: 30,
      generated_at: '2026-05-14T10:30:00+08:00',
      model: isFallback ? 'fallback' : 'glm-4-flash',
      is_fallback: isFallback,
    },
    user: {
      nickname: '示例用户',
      position: '产品主管',
      industry: '互联网',
      ai_usage_purpose: '帮助梳理需求、撰写文档初稿、生成调研要点',
    },
    overall,
    match,
    abilities,
    ...llmNarrative,
    quality: {
      is_fallback: isFallback,
      validator_failures: validatorFailures,
    },
  };
}

// ============================================================================
// 顶层 HTTP 响应包装（done / fallback / generating）
// ============================================================================
export const mockReportRawPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: false,
  is_detailed_unlocked: false,   // 顶层解锁旗标（粗略报告默认 locked，UI 显示"解锁详细报告" CTA）
  user_report_v5: makeUserReportV5({ isFallback: false }),
};

export const mockReportFallbackPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: true,
  is_detailed_unlocked: false,
  user_report_v5: makeUserReportV5({
    isFallback: true,
    validatorFailures: ['llm_call_failed_or_empty'],
  }),
};

export const mockReportGeneratingPayload = {
  status: 'generating',
  report_version: 5,
  phase: 'rough_pending',   // 粗略报告生成中
  is_detailed_unlocked: false,
  user_report_v5: null,
};

// 子块导出，便于单测 / Storybook
export const mockLLMNarrativeBlock = llmNarrative;
export const mockScoringBlock = { overall, abilities };
